<template>
  <div class="cta">
    <EInstaller
      :visible="showOverlay"
      @installer-closed="closeInstaller"
    />
    <EButton
      v-if="buttonText"
      variant="solid-primary"
      :class="{ visible: visible }"
      class="cta__button"
      data-test-id="cta-button"
      :data-track-id="trackId"
      :size="size"
      v-bind="ctaProps"
      @click="install"
    >
      {{ buttonText }}
    </EButton>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import { DISABLE_INSTALL_BROWSERS, SUPPORTED_SAFARI_VERSION } from '~~/common/js/constants/browsers';
import { FACEBOOK_SHARE_URL } from '~~/common/js/constants/urls';
import { getAppOrExtensionUrl } from '~~/common/js/universal/urls';
import EButton from '~~/common/vue/components/button';
import EInstaller from '~~/common/vue/components/installer';

const enabledOverlay = ['firefox', 'ie'];

export default {
  name: 'ECta',
  components: {
    EButton,
    EInstaller,
  },
  props: {
    customTrackId: {
      type: String,
      default: '',
    },
    customCtaText: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'l',
    },
    showShare: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
      showOverlay: false,
    };
  },
  computed: {
    ...mapState('browser', {
      browserName: 'name',
      browserVersion: 'version',
      browserOs: 'os',
    }),
    ...mapState('global', [
      'locale',
    ]),
    ...mapState('user', {
      addOn: 'addOn',
      userBrowser: 'browser',
      userBrowserDisplay: 'browserDisplay',
    }),
    buttonText() {
      if (this.setAsHomepageText) {
        return this.setAsHomepageText;
      }
      if (this.disableInstall) {
        return this.showShare ? this.$t('common.header.counter.share') : '';
      }

      return this.customCtaText ?
        this.customCtaText :
        `${this.$t('common.install.cta')} ${this.userBrowserDisplay}`;
    },
    appExtensionUrl() {
      return getAppOrExtensionUrl({
        os: this.browserOs || '',
        locale: this.locale,
        browser: this.browserName || '',
        version: this.browserVersion,
      });
    },
    hrefAttr() {
      return this.disableInstall && !this.setAsHomepageText ?
        FACEBOOK_SHARE_URL :
        this.appExtensionUrl;
    },
    disableInstall() {
      return this.addOn || DISABLE_INSTALL_BROWSERS.includes(this.userBrowser);
    },
    trackId() {
      let trackEvent = this.disableInstall ? 'share_cta' : 'cta_install';

      if (this.customTrackId) {
        trackEvent = `${trackEvent}_${this.customTrackId}`;
      }

      return trackEvent;
    },
    ctaProps() {
      const browserName = this.browserName || '';
      const target = enabledOverlay.includes(browserName.toLowerCase()) ?
        null :
        '_blank';

      if (browserName.toLowerCase() === 'ie') {
        return {};
      }

      return {
        href: this.hrefAttr,
        as: 'a',
        target,
        rel: 'noopener',
      };
    },
    setAsHomepageText() {
      let text = null;

      if (this.browserName &&
          this.browserVersion &&
          this.browserOs !== 'iOS') {
        const browserName = this.browserName.toLowerCase();
        const browserVersion = parseInt(this.browserVersion, 10);

        if (browserName === 'safari' && browserVersion < SUPPORTED_SAFARI_VERSION) {
          text = this.$t('common.installpopup.title.homepage');
        }
      }

      return text;
    },
  },
  beforeMount() {
    this.assignBrowserInfo();
  },
  mounted() {
    this.visible = true;
  },
  methods: {
    ...mapActions('browser', [
      'assignBrowserInfo',
    ]),
    closeInstaller() {
      this.showOverlay = false;
    },
    install() {
      // The installer function is needed to trigger an
      // overlay for firefox and internet explorer
      if (this.disableInstall ||
          !enabledOverlay.includes(this.browserName.toLowerCase())) {
        return;
      }

      if (this.browserName.toLowerCase() === 'ie') {
        if (window.external && ('AddSearchProvider' in window.external)) {
          window.external.AddSearchProvider(this.hrefAttr);
        }
      }

      this.showOverlay = true;
    },
  },
};
</script>


<style lang="scss" scoped>
.cta {
  display: inline-block;
}

.cta__button {
  @include class-opacity-transition;
}
</style>
